import { ShopifyIntegrationCard } from './ShopifyIntegrationCard';
import { OrderHoldWindowCard } from './OrderHoldWindowCard';
import { XeroIntegrationCard } from './XeroIntegrationCard';
import { AsColourAccountCard } from './AsColourAccountCard';
import { ProductDataLookupCard } from './ProductDataLookupCard';
import { CustomArtworkSourceCard } from './CustomArtworkSourceCard';
import { SlackThreadbotPanel } from './SlackThreadbotPanel';
import { PartnershipsCard } from '@/features/supplier-portal/components/PartnershipsCard';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { trpc } from '@/lib/trpc';

/**
 * Integrations settings tab.
 *
 * Consolidates the integration-type settings that previously lived inside
 * Settings → Integrations: Shopify, Xero, Slack DMs (Threadbot), the Shopify
 * metafield → print-file lookup, and supplier partnerships. Each card is
 * self-contained and drives its own tRPC mutations; nothing here is wired to
 * the Organization tab's profile save form.
 *
 * Pure-client 3PL orgs don't do their own accounting or Slack notifications
 * through the IMS, so Xero and Slack DMs are hidden for them — but only while
 * UNCONFIGURED. Each card holds the only Disconnect / Clear control for its
 * integration, and both nav entries are gated on the connection rather than on org
 * shape, so hiding an active one would leave it running with no way to remove it.
 */
export default function IntegrationsTab() {
  const { isPureClient, isLoading: surfacesLoading } = useOrgRoleSurfaces();
  const xeroStatusQuery = trpc.xero.getStatus.useQuery(undefined, {
    staleTime: 60_000,
  });
  const slackStatusQuery = trpc.slackThreadbot.getStatus.useQuery(undefined, {
    staleTime: 60_000,
  });

  // Don't decide until the org-shape verdict lands, otherwise a client watches
  // these cards render and then vanish — the tab strip above goes to some trouble
  // to avoid exactly that.
  const hideForClient = !surfacesLoading && isPureClient;

  // Both integrations stay visible while CONFIGURED: these cards hold the only
  // Disconnect / Clear controls, and their nav entries are gated on the connection
  // rather than on org shape, so hiding an active one would orphan it.
  const showXero = !hideForClient || (xeroStatusQuery.data?.connected ?? false);
  const showSlack = !hideForClient || (slackStatusQuery.data?.hasBotToken ?? false);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Integrations</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          {hideForClient
            ? 'Connect your store to the external services that power it.'
            : 'Connect Threadheads to the external services that power your store, accounting, notifications, and supplier partnerships.'}
        </p>
      </div>

      <ShopifyIntegrationCard />

      {/* BLU-718 — how long this org's new Shopify orders are held out of
          production so customers can still edit them. Sits next to the Shopify
          card (it's a property of that store's order flow) rather than inside
          it: that card is reused by the onboarding Step 1 and its fields live
          in a single credential form. */}
      <OrderHoldWindowCard />

      {showXero && <XeroIntegrationCard />}

      {/* Per-org Slack DM config (Threadbot for TH; any Slack app for
          other orgs). Multi-tenant from day one — bot token stored in
          Supabase Vault. Powers @-mention notifications on PO + Product
          Dev comments. Self-contained, including its own OAuth-return handler. */}
      {showSlack && <SlackThreadbotPanel />}

      {/* BLU-432 Phase B - Shopify metafield to IMS print-file mapping.
          Self-contained TanStack mutations; admin-gated internally. */}
      <ProductDataLookupCard />

      {/* Phase 60 - Method-C custom artwork source: per-org line-item attribute
          keys carrying the full-res print artwork URL. Dedicated home (NOT a
          metafield row); self-contained mutation; admin-gated internally. */}
      <CustomArtworkSourceCard />

      {/* An org's own AS Colour login. Renders nothing while the backend's mode
          is off or for anyone without org settings. A pure client has no floor to
          set up, so it stays hidden there, unless it holds a connected account:
          this card has the only Disconnect. Waits for the org-shape verdict. */}
      {!surfacesLoading && <AsColourAccountCard hideWhenNotConnected={hideForClient} />}

      {/* Partner portal — redeem a buyer's invite code + manage active
          partnerships. Self-contained mutations; hides itself when the
          feature flag is off. */}
      <PartnershipsCard />
    </div>
  );
}
